import qs from 'qs';
import { Observable, Subscription, Subject, fromEvent, from, throwError } from 'rxjs';
import * as i0 from '@angular/core';
import { Injectable, Pipe, NgModule, InjectionToken, Inject, Directive, PLATFORM_ID, Optional, HostBinding, Input } from '@angular/core';
import { CommonModule, isPlatformBrowser, DOCUMENT } from '@angular/common';
import { Autolinker } from 'autolinker';
import { REQUEST } from '@nguniversal/express-engine/tokens';
import { takeUntil, catchError } from 'rxjs/operators';
import { __decorate, __param, __metadata } from 'tslib';
import axios from 'axios';

/* eslint-disable no-useless-escape */
function filterKeys(params, filterPredicate) {
    const filteredParams = {};
    return Object.keys(params)
        .filter(filterPredicate)
        .reduce((carry, it) => {
        carry[it] = params[it];
        return carry;
    }, filteredParams);
}
function isParamInPath(path, param) {
    return new RegExp(`\\??:${param}`).test(path);
}
function isParamInSpecificPlaceInPath(path, param, position, separator = '/') {
    return path.split(separator)[position].includes(param);
}
function replacePathParams(path, params) {
    return path.replace(/\/\??:[^\/]+/g, (it) => {
        const isOptional = it.charAt(1) === '?';
        const paramName = it.substr((isOptional ? '/?:' : '/:').length);
        if (params[paramName]) {
            return `/${params[paramName]}`;
        }
        if (isOptional) {
            return '';
        }
        throw new Error(`missing parameter ${paramName}`);
    });
}
function buildUrl(path, params = {}) {
    const pathParams = filterKeys(params, (param) => isParamInPath(path, param));
    const qsParams = filterKeys(params, (param) => !isParamInPath(path, param));
    const finalPath = replacePathParams(path, pathParams);
    const queryStringed = qs.stringify(qsParams);
    // eslint-disable-next-line sonarjs/no-nested-template-literals
    return `${finalPath}${queryStringed ? `?${queryStringed}` : ''}`;
}

function downloadFile(blob, filename) {
    // IE doesn't allow using a blob object directly as link href
    // instead it is necessary to use msSaveOrOpenBlob
    if (window.navigator && window.navigator.msSaveOrOpenBlob) {
        window.navigator.msSaveOrOpenBlob(blob, filename);
        return;
    }
    // Create data URL for blob
    const dataUrl = window.URL.createObjectURL(blob);
    // Create anchor element for downloading blob
    const fakeLink = document.createElement('a');
    fakeLink.href = dataUrl;
    fakeLink.download = filename;
    // Dispatch click event on anchor
    // For Firefox it is necessary to delay revoking the ObjectURL
    const fakeEvent = document.createEvent('MouseEvents');
    fakeEvent.initEvent('click', false, true);
    setTimeout(() => fakeLink.dispatchEvent(fakeEvent), 100);
}
function readBlob(blob) {
    return new Observable((subscriber) => {
        const reader = new FileReader();
        reader.onload = () => {
            subscriber.next(reader.result);
            subscriber.complete();
        };
        reader.onerror = (error) => {
            subscriber.error(error);
        };
        reader.readAsArrayBuffer(blob);
    });
}

function buildPath(...paths) {
    return `/${paths.join('/')}`;
}

class DataStorageService {
    put(key, value) {
        if (!this.hasLocalStorage()) {
            return;
        }
        window.localStorage.setItem(key, JSON.stringify(value));
    }
    get(key, Factory) {
        if (!this.hasLocalStorage() || !window.localStorage.getItem(key)) {
            return;
        }
        const data = JSON.parse(window.localStorage.getItem(key));
        if (!Factory) {
            return data;
        }
        if (Array.isArray(data)) {
            return data.map((item) => new Factory(item));
        }
        return new Factory(data);
    }
    remove(key) {
        if (this.hasLocalStorage() && window.localStorage.getItem(key)) {
            window.localStorage.removeItem(key);
        }
    }
    clear() {
        if (!this.hasLocalStorage()) {
            return;
        }
        while (window.localStorage.length > 0) {
            window.localStorage.removeItem(window.localStorage.key(0));
        }
    }
    hasLocalStorage() {
        const test = 'test';
        try {
            localStorage.setItem(test, test);
            localStorage.removeItem(test);
            return true;
        }
        catch (e) {
            return false;
        }
    }
}
DataStorageService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.0.1", ngImport: i0, type: DataStorageService, deps: [], target: i0.ɵɵFactoryTarget.Injectable });
DataStorageService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "14.0.1", ngImport: i0, type: DataStorageService });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.0.1", ngImport: i0, type: DataStorageService, decorators: [{
            type: Injectable
        }] });

class DataStorage {
}

class AbstractToFormControlPipe {
    transform(value) {
        return value;
    }
}
AbstractToFormControlPipe.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.0.1", ngImport: i0, type: AbstractToFormControlPipe, deps: [], target: i0.ɵɵFactoryTarget.Pipe });
AbstractToFormControlPipe.ɵpipe = i0.ɵɵngDeclarePipe({ minVersion: "14.0.0", version: "14.0.1", ngImport: i0, type: AbstractToFormControlPipe, name: "abstractToFormControl" });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.0.1", ngImport: i0, type: AbstractToFormControlPipe, decorators: [{
            type: Pipe,
            args: [{
                    name: 'abstractToFormControl',
                }]
        }] });
class AbstractToFormControlPipeModule {
}
AbstractToFormControlPipeModule.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.0.1", ngImport: i0, type: AbstractToFormControlPipeModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
AbstractToFormControlPipeModule.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "14.0.1", ngImport: i0, type: AbstractToFormControlPipeModule, declarations: [AbstractToFormControlPipe], exports: [AbstractToFormControlPipe] });
AbstractToFormControlPipeModule.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "14.0.1", ngImport: i0, type: AbstractToFormControlPipeModule });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.0.1", ngImport: i0, type: AbstractToFormControlPipeModule, decorators: [{
            type: NgModule,
            args: [{
                    exports: [AbstractToFormControlPipe],
                    declarations: [AbstractToFormControlPipe],
                }]
        }] });

class KeysPipe {
    transform(value) {
        return Object.keys(value || {});
    }
}
KeysPipe.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.0.1", ngImport: i0, type: KeysPipe, deps: [], target: i0.ɵɵFactoryTarget.Pipe });
KeysPipe.ɵpipe = i0.ɵɵngDeclarePipe({ minVersion: "14.0.0", version: "14.0.1", ngImport: i0, type: KeysPipe, name: "wizKeys" });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.0.1", ngImport: i0, type: KeysPipe, decorators: [{
            type: Pipe,
            args: [{
                    name: 'wizKeys',
                }]
        }] });
class KeysPipeModule {
}
KeysPipeModule.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.0.1", ngImport: i0, type: KeysPipeModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
KeysPipeModule.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "14.0.1", ngImport: i0, type: KeysPipeModule, declarations: [KeysPipe], imports: [CommonModule], exports: [KeysPipe] });
KeysPipeModule.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "14.0.1", ngImport: i0, type: KeysPipeModule, imports: [CommonModule] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.0.1", ngImport: i0, type: KeysPipeModule, decorators: [{
            type: NgModule,
            args: [{
                    imports: [CommonModule],
                    declarations: [KeysPipe],
                    exports: [KeysPipe],
                }]
        }] });

class LinkyPipe {
    transform(value, options) {
        return value ? Autolinker.link(value, options) : value;
    }
}
LinkyPipe.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.0.1", ngImport: i0, type: LinkyPipe, deps: [], target: i0.ɵɵFactoryTarget.Pipe });
LinkyPipe.ɵpipe = i0.ɵɵngDeclarePipe({ minVersion: "14.0.0", version: "14.0.1", ngImport: i0, type: LinkyPipe, name: "wizLinky" });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.0.1", ngImport: i0, type: LinkyPipe, decorators: [{
            type: Pipe,
            args: [{
                    name: 'wizLinky',
                }]
        }] });
class LinkyPipeModule {
}
LinkyPipeModule.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.0.1", ngImport: i0, type: LinkyPipeModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
LinkyPipeModule.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "14.0.1", ngImport: i0, type: LinkyPipeModule, declarations: [LinkyPipe], imports: [CommonModule], exports: [LinkyPipe] });
LinkyPipeModule.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "14.0.1", ngImport: i0, type: LinkyPipeModule, imports: [CommonModule] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.0.1", ngImport: i0, type: LinkyPipeModule, decorators: [{
            type: NgModule,
            args: [{
                    imports: [CommonModule],
                    declarations: [LinkyPipe],
                    exports: [LinkyPipe],
                }]
        }] });

/* eslint-disable no-useless-escape */
class SlugifyPipe {
    constructor() {
        this.from = 'ãàáäâẽèéëêìíïîõòóöôùúüûñç·/_,:;';
        this.to = 'aaaaaeeeeeiiiiooooouuuunc------';
    }
    transform(value) {
        let text = value;
        for (let i = 0; i < this.from.length; i++) {
            text = text.replace(new RegExp(this.from.charAt(i), 'g'), this.to.charAt(i));
        }
        return text
            .toString()
            .toLowerCase()
            .trim()
            .replace(/\s+/g, '-')
            .replace(/&/g, '-y-')
            .replace(/[^\w\-]+/g, '')
            .replace(/\-\-+/g, '-');
    }
}
SlugifyPipe.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.0.1", ngImport: i0, type: SlugifyPipe, deps: [], target: i0.ɵɵFactoryTarget.Pipe });
SlugifyPipe.ɵpipe = i0.ɵɵngDeclarePipe({ minVersion: "14.0.0", version: "14.0.1", ngImport: i0, type: SlugifyPipe, name: "wizSlugify" });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.0.1", ngImport: i0, type: SlugifyPipe, decorators: [{
            type: Pipe,
            args: [{
                    name: 'wizSlugify',
                }]
        }] });
class SlugifyPipeModule {
}
SlugifyPipeModule.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.0.1", ngImport: i0, type: SlugifyPipeModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
SlugifyPipeModule.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "14.0.1", ngImport: i0, type: SlugifyPipeModule, declarations: [SlugifyPipe], imports: [CommonModule], exports: [SlugifyPipe] });
SlugifyPipeModule.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "14.0.1", ngImport: i0, type: SlugifyPipeModule, imports: [CommonModule] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.0.1", ngImport: i0, type: SlugifyPipeModule, decorators: [{
            type: NgModule,
            args: [{
                    imports: [CommonModule],
                    declarations: [SlugifyPipe],
                    exports: [SlugifyPipe],
                }]
        }] });

class WizPipesModule {
}
WizPipesModule.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.0.1", ngImport: i0, type: WizPipesModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
WizPipesModule.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "14.0.1", ngImport: i0, type: WizPipesModule, imports: [CommonModule, SlugifyPipeModule, LinkyPipeModule, KeysPipeModule, AbstractToFormControlPipeModule] });
WizPipesModule.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "14.0.1", ngImport: i0, type: WizPipesModule, imports: [CommonModule, SlugifyPipeModule, LinkyPipeModule, KeysPipeModule, AbstractToFormControlPipeModule] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.0.1", ngImport: i0, type: WizPipesModule, decorators: [{
            type: NgModule,
            args: [{
                    imports: [CommonModule, SlugifyPipeModule, LinkyPipeModule, KeysPipeModule, AbstractToFormControlPipeModule],
                }]
        }] });

const IMAGINARY_PIPE_CONFIG = new InjectionToken('IMAGINARY_PIPE_CONFIG');

var OperationTypes;
(function (OperationTypes) {
    OperationTypes["RESIZE"] = "resize";
    OperationTypes["EXTRACT"] = "extract";
    OperationTypes["SMARTCROP"] = "smartcrop";
    OperationTypes["CONVERT"] = "convert";
    OperationTypes["CROP"] = "crop";
})(OperationTypes || (OperationTypes = {}));
var GravityEnum;
(function (GravityEnum) {
    GravityEnum["North"] = "north";
    GravityEnum["South"] = "south";
    GravityEnum["Centre"] = "centre";
    GravityEnum["East"] = "east";
    GravityEnum["West"] = "west";
    GravityEnum["Smart"] = "smart";
})(GravityEnum || (GravityEnum = {}));
/**
 * ImageService offers an interface for Imaginary API (https://github.com/h2non/imaginary)
 * The constructor url should be set dynamically according to the used environment
 */
class ImageService {
    constructor(url) {
        // Remove trailing slash
        this.imaginaryUrl = url.replace(/\/$/, '');
    }
    /**
     * smartcrop should be used to crop an image without resizing its content
     *
     * Imaginary automatically detects the point of interest around which the crop should be executed,
     * hence why no coordinates are necessary
     *
     * @param imageUrl
     * @param smartCropParams
     */
    smartcrop(imageUrl, smartCropParams) {
        this.validateDimensions(smartCropParams);
        const queryParams = this.buildWithQueryParams({ url: imageUrl, type: 'auto', ...smartCropParams });
        return `${this.imaginaryUrl}/${OperationTypes.SMARTCROP}?${queryParams}`;
    }
    /**
     * crop should be used to crop an image without resizing its content
     *
     * Imaginary automatically detects the point of interest around which the crop should be executed,
     * hence why no coordinates are necessary
     *
     * @param imageUrl
     * @param smartCropParams
     */
    crop(imageUrl, cropParams) {
        this.validateDimensions(cropParams);
        const queryParams = this.buildWithQueryParams({ url: imageUrl, type: 'auto', ...cropParams });
        return `${this.imaginaryUrl}/${OperationTypes.CROP}?${queryParams}`;
    }
    /**
     * resize should be used to alter the size of an image without altering its content
     *
     * 'nocrop' mode is forced when using this method, which means the image's ratio is kept.
     * You should therefore only specify either a width or an height depending on the new dimensions you need
     *
     * @param imageUrl
     * @param resizeParams
     */
    resize(imageUrl, resizeParams) {
        this.validateDimensions(resizeParams);
        const queryParams = this.buildWithQueryParams({ url: imageUrl, type: 'auto', ...resizeParams, nocrop: true });
        return `${this.imaginaryUrl}/${OperationTypes.RESIZE}?${queryParams}`;
    }
    /**
     * resize should be used to alter the size of an image without altering its content
     *
     * 'nocrop' mode is forced when using this method, which means the image's ratio is kept.
     * You should therefore only specify either a width or an height depending on the new dimensions you need
     *
     * @param imageUrl
     * @param resizeParams
     */
    convert(imageUrl, commonParams) {
        const queryParams = this.buildWithQueryParams({ url: imageUrl, type: 'auto', ...commonParams });
        return `${this.imaginaryUrl}/${OperationTypes.CONVERT}?${queryParams}`;
    }
    /**
     * pipe should be used to pipe different operations allowed by the Imaginary API
     * Keep in mind the pipe method is limited to 10 operations maximum.
     *
     * i.e.: 1st OP: Extract Operation -> 2nd OP: Resize Operation
     * i.e.: 1st OP: Smartcrop Operation -> 2nd OP: Resize Operation
     *
     * @param imageUrl
     * @param pipelineOperations
     */
    pipe(imageUrl, pipelineOperations) {
        if (pipelineOperations.length > 10) {
            throw new Error('pipe method is limited to 10 operations maximum');
        }
        const queryParams = this.buildWithQueryParams({
            url: imageUrl,
            type: 'auto',
            operations: JSON.stringify(pipelineOperations),
        });
        return `${this.imaginaryUrl}/pipeline?${queryParams}`;
    }
    /**
     * generate a src set of 4 resolutions (1x, 2x, 3x, 4x) for the given image
     *
     * @param imageUrl
     * @param resizeParams
     */
    getSrcSet(imageUrl, resizeParams) {
        const defaultParams = {
            type: resizeParams.type,
            quality: resizeParams.quality,
        };
        const image1x = this.resize(imageUrl, {
            ...defaultParams,
            width: resizeParams.width,
            ...(resizeParams.height ? { height: resizeParams.height } : {}),
        });
        const image2x = this.resize(imageUrl, {
            ...defaultParams,
            width: resizeParams.width * 2,
            ...(resizeParams.height ? { height: resizeParams.height * 2 } : {}),
        });
        const image3x = this.resize(imageUrl, {
            ...defaultParams,
            width: resizeParams.width * 3,
            ...(resizeParams.height ? { height: resizeParams.height * 3 } : {}),
        });
        const image4x = this.resize(imageUrl, {
            ...defaultParams,
            width: resizeParams.width * 4,
            ...(resizeParams.height ? { height: resizeParams.height * 4 } : {}),
        });
        return { image1x, image2x, image3x, image4x };
    }
    validateDimensions(dimensions) {
        if (!dimensions.width && !dimensions.height) {
            throw new Error('dimensions should have at least a width or an height');
        }
    }
    buildWithQueryParams(queryParams) {
        return qs.stringify(queryParams);
    }
}

class ImaginarySrcPipe {
    constructor({ imaginaryUrl }) {
        this.imageService = new ImageService(imaginaryUrl);
    }
    transform(imageUrl, ratio, width, height) {
        let imageSrcSet = '';
        if (ratio > 1) {
            for (let index = 1; index <= ratio; index++) {
                const imageSrc = this.imageService.resize(imageUrl, {
                    width: width * index,
                    height: height ? height * index : undefined,
                });
                imageSrcSet += index === ratio ? `${imageSrc} ${index}x` : `${imageSrc} ${index}x, `;
            }
            return imageSrcSet;
        }
        return this.imageService.resize(imageUrl, {
            width,
            height: height ? height : undefined,
        });
    }
}
ImaginarySrcPipe.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.0.1", ngImport: i0, type: ImaginarySrcPipe, deps: [{ token: IMAGINARY_PIPE_CONFIG }], target: i0.ɵɵFactoryTarget.Pipe });
ImaginarySrcPipe.ɵpipe = i0.ɵɵngDeclarePipe({ minVersion: "14.0.0", version: "14.0.1", ngImport: i0, type: ImaginarySrcPipe, name: "imaginarySrc" });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.0.1", ngImport: i0, type: ImaginarySrcPipe, decorators: [{
            type: Pipe,
            args: [{
                    name: 'imaginarySrc',
                }]
        }], ctorParameters: function () { return [{ type: undefined, decorators: [{
                    type: Inject,
                    args: [IMAGINARY_PIPE_CONFIG]
                }] }]; } });
class ImaginarySrcModule {
}
ImaginarySrcModule.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.0.1", ngImport: i0, type: ImaginarySrcModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
ImaginarySrcModule.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "14.0.1", ngImport: i0, type: ImaginarySrcModule, declarations: [ImaginarySrcPipe], imports: [CommonModule], exports: [ImaginarySrcPipe] });
ImaginarySrcModule.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "14.0.1", ngImport: i0, type: ImaginarySrcModule, imports: [CommonModule] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.0.1", ngImport: i0, type: ImaginarySrcModule, decorators: [{
            type: NgModule,
            args: [{
                    imports: [CommonModule],
                    exports: [ImaginarySrcPipe],
                    declarations: [ImaginarySrcPipe],
                }]
        }] });

class AutofocusDirective {
    constructor(element) {
        this.element = element;
    }
    ngAfterViewInit() {
        this.element.nativeElement.focus();
    }
}
AutofocusDirective.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.0.1", ngImport: i0, type: AutofocusDirective, deps: [{ token: i0.ElementRef }], target: i0.ɵɵFactoryTarget.Directive });
AutofocusDirective.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "14.0.0", version: "14.0.1", type: AutofocusDirective, selector: "[wizAutofocus]", ngImport: i0 });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.0.1", ngImport: i0, type: AutofocusDirective, decorators: [{
            type: Directive,
            args: [{
                    selector: '[wizAutofocus]',
                }]
        }], ctorParameters: function () { return [{ type: i0.ElementRef }]; } });

class WizAutofocusModule {
}
WizAutofocusModule.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.0.1", ngImport: i0, type: WizAutofocusModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
WizAutofocusModule.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "14.0.1", ngImport: i0, type: WizAutofocusModule, declarations: [AutofocusDirective], imports: [CommonModule], exports: [AutofocusDirective] });
WizAutofocusModule.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "14.0.1", ngImport: i0, type: WizAutofocusModule, imports: [CommonModule] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.0.1", ngImport: i0, type: WizAutofocusModule, decorators: [{
            type: NgModule,
            args: [{
                    imports: [CommonModule],
                    declarations: [AutofocusDirective],
                    exports: [AutofocusDirective],
                }]
        }] });

/* eslint-disable no-prototype-builtins */
/* eslint-disable @typescript-eslint/explicit-module-boundary-types */
/* eslint no-console: ["error", { allow: ["warn", "error"] }] */
const services = [
    (event) => {
        if (window.wa === undefined) {
            console.warn('wa is not loaded');
            return;
        }
        const data = {
            hitType: 'event',
            eventCategory: event.event,
            eventAction: event.category,
            eventLabel: event.label,
            eventValue: '',
        };
        if (event.hasOwnProperty('value')) {
            data.eventValue = event.value;
        }
        window.wa('track', data);
    },
    (event) => {
        if (window.ga === undefined) {
            console.warn('ga is not loaded');
            return;
        }
        const data = {
            eventCategory: event.event,
            eventAction: event.category,
            eventLabel: event.label,
            eventValue: '',
        };
        if (event.hasOwnProperty('value')) {
            data.eventValue = event.value;
        }
        window.ga('send', 'event', data);
    },
    (event) => {
        if (window.mixpanel === undefined) {
            return;
        }
        window.mixpanel.track(event.event, {
            Action: event.category,
            Label: event.label,
        });
    },
];
function collect(event) {
    const newGenProps = ['category', 'action', 'label'];
    if (newGenProps.every((name) => {
        return event.hasOwnProperty(name);
    })) {
        event.event = event.category;
        event.category = event.action;
    }
    const requiredProps = ['event', 'category', 'label'];
    if (requiredProps.some((name) => {
        return !event.hasOwnProperty(name);
    })) {
        console.warn(`cannot send event as it misses one of "${requiredProps.join(', ')}"`, event);
        return;
    }
    services.forEach((service) => {
        return service(event);
    });
}
function addService(service) {
    return services.push(service);
}

/**
 * getAge calculates an age in year based on given birthDate
 * @param birthDate: Date
 * @return number
 */
function getAge(birthDate) {
    const today = new Date();
    const age = Math.abs(today.getFullYear() - birthDate.getFullYear());
    if (today.getMonth() > birthDate.getMonth() ||
        (today.getMonth() === birthDate.getMonth() && today.getDate() >= birthDate.getDate())) {
        return age;
    }
    return age - 1;
}

/* eslint-disable @typescript-eslint/ban-types */
// Create a new injection token for injecting the window into a component.
const WINDOW = new InjectionToken('WindowToken');
// Define abstract class for obtaining reference to the global window object.
class WindowRef {
    get nativeWindow() {
        throw new Error('Not implemented.');
    }
}
// Define class that implements the abstract class and returns the native window object.
class BrowserWindowRef extends WindowRef {
    constructor() {
        super();
    }
    get nativeWindow() {
        return window;
    }
}
// Create an factory function that returns the native window object.
function windowFactory(browserWindowRef, platformId, request) {
    if (isPlatformBrowser(platformId) && browserWindowRef && browserWindowRef.nativeWindow) {
        return browserWindowRef.nativeWindow;
    }
    const noop = () => { };
    return {
        location: {
            origin: request ? `${request.protocol}://${request.get('host')}` : '',
            pathname: request ? request.url : '',
            assign: noop,
        },
        performance: {
            now: noop,
        },
        setInterval: noop,
        clearInterval: noop,
        scrollTo: noop,
        requestAnimationFrame: noop,
        addEventListener: noop,
        removeEventListener: noop,
        devicePixelRatio: 1,
        navigator: { platform: '' },
        getComputedStyle: () => ({ getPropertyValue: () => '' }),
    };
}
// Create a injectable provider for the WindowRef token that uses the BrowserWindowRef class.
const browserWindowProvider = {
    provide: WindowRef,
    useClass: BrowserWindowRef,
};
// Create an injectable provider that uses the windowFactory function for returning the native window object.
const windowProvider = {
    provide: WINDOW,
    useFactory: windowFactory,
    deps: [WindowRef, PLATFORM_ID, [new Optional(), REQUEST]],
};
// Create an array of providers.
const WINDOW_PROVIDERS = [browserWindowProvider, windowProvider];

class LazyImgDirective {
    constructor(el, platformId, window, cdr, ngZone) {
        this.el = el;
        this.platformId = platformId;
        this.window = window;
        this.cdr = cdr;
        this.ngZone = ngZone;
        this.srcAttr = null;
        this.srcsetAttr = null;
        this.lazyOffset = '500px';
    }
    ngAfterViewInit() {
        this.lazyLoad();
    }
    lazyLoad() {
        if (isPlatformBrowser(this.platformId)) {
            if (this.hasCompatibleBrowser()) {
                this.lazyLoadImage();
            }
            else {
                this.loadImage();
            }
        }
    }
    ngOnChanges(changes) {
        if (['lazySrc', 'lazySrcset', 'lazyOffset'].some((key) => changes[key].currentValue !== changes[key].previousValue)) {
            this.lazyLoad();
        }
    }
    hasCompatibleBrowser() {
        const hasIntersectionObserver = this.window && 'IntersectionObserver' in this.window;
        const userAgent = this.window.navigator.userAgent;
        const matches = userAgent.match(/Edge\/(\d*)\./i);
        const isEdge = !!matches && matches.length > 1;
        const isEdgeVersion16OrBetter = isEdge && !!matches && parseInt(matches[1], 10) > 15;
        return hasIntersectionObserver && (!isEdge || isEdgeVersion16OrBetter);
    }
    lazyLoadImage() {
        // Improve performance
        this.ngZone.runOutsideAngular(() => {
            const obs = new IntersectionObserver((entries) => {
                entries.forEach(({ isIntersecting }) => {
                    if (isIntersecting) {
                        this.loadImage();
                        obs.unobserve(this.el.nativeElement);
                    }
                });
            }, {
                rootMargin: this.lazyOffset, // Load Img slightly before being in viewport
            });
            obs.observe(this.el.nativeElement);
        });
    }
    loadImage() {
        this.srcAttr = this.lazySrc;
        this.srcsetAttr = this.lazySrcset;
        this.cdr.detectChanges();
    }
}
LazyImgDirective.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.0.1", ngImport: i0, type: LazyImgDirective, deps: [{ token: i0.ElementRef }, { token: PLATFORM_ID }, { token: WINDOW }, { token: i0.ChangeDetectorRef }, { token: i0.NgZone }], target: i0.ɵɵFactoryTarget.Directive });
LazyImgDirective.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "14.0.0", version: "14.0.1", type: LazyImgDirective, selector: "img[wizLazyImg]", inputs: { lazySrc: "lazySrc", lazySrcset: "lazySrcset", lazyOffset: "lazyOffset" }, host: { properties: { "attr.src": "this.srcAttr", "attr.srcset": "this.srcsetAttr" } }, usesOnChanges: true, ngImport: i0 });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.0.1", ngImport: i0, type: LazyImgDirective, decorators: [{
            type: Directive,
            args: [{
                    selector: 'img[wizLazyImg]',
                }]
        }], ctorParameters: function () { return [{ type: i0.ElementRef }, { type: undefined, decorators: [{
                    type: Inject,
                    args: [PLATFORM_ID]
                }] }, { type: undefined, decorators: [{
                    type: Inject,
                    args: [WINDOW]
                }] }, { type: i0.ChangeDetectorRef }, { type: i0.NgZone }]; }, propDecorators: { srcAttr: [{
                type: HostBinding,
                args: ['attr.src']
            }], srcsetAttr: [{
                type: HostBinding,
                args: ['attr.srcset']
            }], lazySrc: [{
                type: Input
            }], lazySrcset: [{
                type: Input
            }], lazyOffset: [{
                type: Input
            }] } });
class LazyImgModule {
}
LazyImgModule.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.0.1", ngImport: i0, type: LazyImgModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
LazyImgModule.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "14.0.1", ngImport: i0, type: LazyImgModule, declarations: [LazyImgDirective], imports: [CommonModule], exports: [LazyImgDirective] });
LazyImgModule.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "14.0.1", ngImport: i0, type: LazyImgModule, imports: [CommonModule] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.0.1", ngImport: i0, type: LazyImgModule, decorators: [{
            type: NgModule,
            args: [{
                    imports: [CommonModule],
                    declarations: [LazyImgDirective],
                    exports: [LazyImgDirective],
                }]
        }] });

class LazyVideoDirective {
    constructor(el, platformId, window, cdr, ngZone) {
        this.el = el;
        this.platformId = platformId;
        this.window = window;
        this.cdr = cdr;
        this.ngZone = ngZone;
        this.preloadAttr = 'none';
        this.lazyOffset = '500px';
    }
    ngAfterViewInit() {
        if (isPlatformBrowser(this.platformId)) {
            if (this.hasCompatibleBrowser()) {
                this.lazyLoadVideo();
            }
            else {
                this.loadVideo();
            }
        }
    }
    hasCompatibleBrowser() {
        const hasIntersectionObserver = this.window && 'IntersectionObserver' in this.window;
        const userAgent = this.window.navigator.userAgent;
        const matches = userAgent.match(/Edge\/(\d*)\./i);
        const isEdge = !!matches && matches.length > 1;
        const isEdgeVersion16OrBetter = isEdge && !!matches && parseInt(matches[1], 10) > 15;
        return hasIntersectionObserver && (!isEdge || isEdgeVersion16OrBetter);
    }
    lazyLoadVideo() {
        // Improve performance
        this.ngZone.runOutsideAngular(() => {
            const obs = new IntersectionObserver((entries) => {
                entries.forEach(({ isIntersecting }) => {
                    if (isIntersecting) {
                        this.loadVideo();
                        obs.unobserve(this.el.nativeElement);
                    }
                });
            }, {
                rootMargin: this.lazyOffset, // Load Img slightly before being in viewport
            });
            obs.observe(this.el.nativeElement);
        });
    }
    loadVideo() {
        this.preloadAttr = this.preload;
        this.cdr.detectChanges();
    }
}
LazyVideoDirective.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.0.1", ngImport: i0, type: LazyVideoDirective, deps: [{ token: i0.ElementRef }, { token: PLATFORM_ID }, { token: WINDOW }, { token: i0.ChangeDetectorRef }, { token: i0.NgZone }], target: i0.ɵɵFactoryTarget.Directive });
LazyVideoDirective.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "14.0.0", version: "14.0.1", type: LazyVideoDirective, selector: "video[wizLazyVideo]", inputs: { preload: "preload", lazyOffset: "lazyOffset" }, host: { properties: { "attr.preload": "this.preloadAttr" } }, ngImport: i0 });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.0.1", ngImport: i0, type: LazyVideoDirective, decorators: [{
            type: Directive,
            args: [{
                    selector: 'video[wizLazyVideo]',
                }]
        }], ctorParameters: function () { return [{ type: i0.ElementRef }, { type: undefined, decorators: [{
                    type: Inject,
                    args: [PLATFORM_ID]
                }] }, { type: undefined, decorators: [{
                    type: Inject,
                    args: [WINDOW]
                }] }, { type: i0.ChangeDetectorRef }, { type: i0.NgZone }]; }, propDecorators: { preloadAttr: [{
                type: HostBinding,
                args: ['attr.preload']
            }], preload: [{
                type: Input
            }], lazyOffset: [{
                type: Input
            }] } });
class LazyVideoModule {
}
LazyVideoModule.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.0.1", ngImport: i0, type: LazyVideoModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
LazyVideoModule.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "14.0.1", ngImport: i0, type: LazyVideoModule, declarations: [LazyVideoDirective], imports: [CommonModule], exports: [LazyVideoDirective] });
LazyVideoModule.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "14.0.1", ngImport: i0, type: LazyVideoModule, imports: [CommonModule] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.0.1", ngImport: i0, type: LazyVideoModule, decorators: [{
            type: NgModule,
            args: [{
                    imports: [CommonModule],
                    declarations: [LazyVideoDirective],
                    exports: [LazyVideoDirective],
                }]
        }] });

class AosService {
    constructor(window, platformId) {
        this.window = window;
        this.scrollSub = new Subscription();
        this.resizeSub = new Subscription();
        this.destroyed$ = new Subject();
        if (!isPlatformBrowser(platformId)) {
            return;
        }
        this.manageScrollPos();
        this.scrollObs = fromEvent(this.window, 'scroll');
        this.scrollSub = this.scrollObs.pipe(takeUntil(this.destroyed$)).subscribe(() => this.manageScrollPos());
        this.resizeObs = fromEvent(this.window, 'resize');
        this.resizeSub = this.resizeObs.pipe(takeUntil(this.destroyed$)).subscribe(() => this.manageScrollPos());
    }
    manageScrollPos() {
        this.pos = this.window.pageYOffset;
    }
    ngOnDestroy() {
        this.destroyed$.next(null);
    }
}
AosService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.0.1", ngImport: i0, type: AosService, deps: [{ token: WINDOW }, { token: PLATFORM_ID }], target: i0.ɵɵFactoryTarget.Injectable });
AosService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "14.0.1", ngImport: i0, type: AosService });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.0.1", ngImport: i0, type: AosService, decorators: [{
            type: Injectable
        }], ctorParameters: function () { return [{ type: undefined, decorators: [{
                    type: Inject,
                    args: [WINDOW]
                }] }, { type: undefined, decorators: [{
                    type: Inject,
                    args: [PLATFORM_ID]
                }] }]; } });

class AosDirective {
    constructor(elementRef, renderer, scroll, window, platformId) {
        this.elementRef = elementRef;
        this.renderer = renderer;
        this.scroll = scroll;
        this.window = window;
        this.platformId = platformId;
        this.scrollSub = new Subscription();
        this.resizeSub = new Subscription();
        this.destroyed$ = new Subject();
        this.offset = 80;
        this.defaultInit = true;
        this.elementClass = this.defaultInit; // Used to preset styles to prevent adding "directive specific" style in component.
    }
    get id() {
        return this.elementRef.nativeElement.id;
    }
    ngOnInit() {
        if (!isPlatformBrowser(this.platformId)) {
            return;
        }
        if (!this.animationName) {
            throw new Error('animationName required');
        }
        this.elementClass = this.defaultInit;
        this.isVisible = false;
        this.scrollSub = this.scroll.scrollObs.pipe(takeUntil(this.destroyed$)).subscribe(() => this.manageVisibility());
        // subscribe to resize event using service so scrolling position is always accurate
        this.resizeSub = this.scroll.resizeObs.pipe(takeUntil(this.destroyed$)).subscribe(() => this.manageVisibility());
    }
    ngAfterViewInit() {
        setTimeout(() => this.manageVisibility(), 1);
    }
    ngOnDestroy() {
        this.destroyed$.next(null);
    }
    manageVisibility() {
        if (this.isVisible || !isPlatformBrowser(this.platformId)) {
            return;
        }
        this.setWinHeight();
        this.setOffsetTop();
        const scrollTrigger = this.offsetTop + this.offset - this.winHeight;
        if (this.scroll.pos >= scrollTrigger) {
            this.addAnimationClass();
        }
    }
    addAnimationClass() {
        this.isVisible = true;
        this.setClass(this.animationName);
    }
    setClass(classes) {
        for (const c of classes.split(' ')) {
            this.renderer.addClass(this.elementRef.nativeElement, c);
        }
    }
    setWinHeight() {
        this.winHeight = this.window.innerHeight;
    }
    setOffsetTop() {
        if (!isPlatformBrowser(this.platformId)) {
            return;
        }
        const viewportTop = this.elementRef.nativeElement.getBoundingClientRect().top;
        const clientTop = this.elementRef.nativeElement.clientTop;
        this.offsetTop = +viewportTop + +this.scroll.pos - +clientTop;
    }
}
AosDirective.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.0.1", ngImport: i0, type: AosDirective, deps: [{ token: i0.ElementRef }, { token: i0.Renderer2 }, { token: AosService }, { token: WINDOW }, { token: PLATFORM_ID }], target: i0.ɵɵFactoryTarget.Directive });
AosDirective.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "14.0.0", version: "14.0.1", type: AosDirective, selector: "[wizAos]", inputs: { animationName: "animationName", offset: "offset", defaultInit: "defaultInit" }, host: { properties: { "class.aos-init": "this.elementClass" } }, ngImport: i0 });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.0.1", ngImport: i0, type: AosDirective, decorators: [{
            type: Directive,
            args: [{
                    selector: '[wizAos]',
                }]
        }], ctorParameters: function () { return [{ type: i0.ElementRef }, { type: i0.Renderer2 }, { type: AosService }, { type: undefined, decorators: [{
                    type: Inject,
                    args: [WINDOW]
                }] }, { type: undefined, decorators: [{
                    type: Inject,
                    args: [PLATFORM_ID]
                }] }]; }, propDecorators: { animationName: [{
                type: Input
            }], offset: [{
                type: Input
            }], defaultInit: [{
                type: Input
            }], elementClass: [{
                type: HostBinding,
                args: ['class.aos-init']
            }] } });
class AosModule {
    static forRoot() {
        return {
            ngModule: AosModule,
            providers: [AosService],
        };
    }
}
AosModule.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.0.1", ngImport: i0, type: AosModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
AosModule.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "14.0.1", ngImport: i0, type: AosModule, declarations: [AosDirective], imports: [CommonModule], exports: [AosDirective] });
AosModule.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "14.0.1", ngImport: i0, type: AosModule, imports: [CommonModule] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.0.1", ngImport: i0, type: AosModule, decorators: [{
            type: NgModule,
            args: [{
                    imports: [CommonModule],
                    declarations: [AosDirective],
                    exports: [AosDirective],
                }]
        }] });

/**
 * Manages the scroll position for a browser window.
 */
let AppBrowserViewportScroller = class AppBrowserViewportScroller {
    constructor(document, window) {
        this.document = document;
        this.window = window;
        this.offset = () => [0, 0];
    }
    /**
     * Configures the top offset used when scrolling to an anchor.
     * @param offset A position in screen coordinates (a tuple with x and y values)
     * or a function that returns the top offset position.
     *
     */
    setOffset(offset) {
        if (Array.isArray(offset)) {
            this.offset = () => offset;
        }
        else {
            this.offset = offset;
        }
    }
    /**
     * Retrieves the current scroll position.
     * @returns The position in screen coordinates.
     */
    getScrollPosition() {
        if (this.supportScrollRestoration()) {
            return [this.window.scrollX, this.window.scrollY];
        }
        return [0, 0];
    }
    /**
     * Sets the scroll position.
     * @param position The new position in screen coordinates.
     */
    scrollToPosition(position) {
        if (this.supportScrollRestoration()) {
            this.window.scrollTo(position[0], position[1]);
        }
    }
    /**
     * Scrolls to an anchor element.
     * @param anchor The ID of the anchor element.
     */
    scrollToAnchor(anchor) {
        if (this.supportScrollRestoration()) {
            const elSelectedById = this.document.getElementById(anchor);
            if (elSelectedById) {
                this.scrollToElement(elSelectedById);
                return;
            }
            const elSelectedByName = this.document.querySelector(`[name='${anchor}']`);
            if (elSelectedByName) {
                this.scrollToElement(elSelectedByName);
                return;
            }
        }
    }
    /**
     * Disables automatic scroll restoration provided by the browser.
     */
    setHistoryScrollRestoration(scrollRestoration) {
        if (this.supportScrollRestoration()) {
            const history = this.window.history;
            if (history && history.scrollRestoration) {
                history.scrollRestoration = scrollRestoration;
            }
        }
    }
    scrollToElement(el) {
        const rect = el.getBoundingClientRect();
        const left = rect.left + this.window.pageXOffset;
        const top = rect.top + this.window.pageYOffset;
        const offset = this.offset();
        this.window.scrollTo(left - offset[0], top - offset[1]);
    }
    /**
     * We only support scroll restoration when we can get a hold of window.
     * This means that we do not support this behavior when running in a web worker.
     *
     * Lifting this restriction right now would require more changes in the dom adapter.
     * Since webworkers aren't widely used, we will lift it once RouterScroller is
     * battle-tested.
     */
    supportScrollRestoration() {
        try {
            if (!this.window || !this.window.scrollTo) {
                return false;
            }
            const scrollRestorationDescriptor = Object.getOwnPropertyDescriptor(this.window.history, 'scrollRestoration');
            return !!scrollRestorationDescriptor && !!scrollRestorationDescriptor.writable;
        }
        catch {
            return false;
        }
    }
};
AppBrowserViewportScroller = __decorate([
    __param(0, Inject(DOCUMENT)),
    __param(1, Inject(WINDOW)),
    __metadata("design:paramtypes", [Object, Object])
], AppBrowserViewportScroller);

/**
 * Provides an empty implementation of the viewport scroller. This will
 * live in @angular/common as it will be used by both platform-server and platform-webworker.
 */
class AppNullViewportScroller {
    /**
     * Empty implementation
     */
    setOffset(_) { }
    /**
     * Empty implementation
     */
    getScrollPosition() {
        return [0, 0];
    }
    /**
     * Empty implementation
     */
    scrollToPosition(_) { }
    /**
     * Empty implementation
     */
    scrollToAnchor(_) { }
    /**
     * Empty implementation
     */
    setHistoryScrollRestoration(_) { }
}

/* eslint-disable sonarjs/no-duplicated-branches */
/* eslint-disable no-empty */
function reverseString(str) {
    return str.split('').reverse().join('');
}
var PasswordStrength;
(function (PasswordStrength) {
    PasswordStrength[PasswordStrength["TooShort"] = 0] = "TooShort";
    PasswordStrength[PasswordStrength["VeryWeak"] = 1] = "VeryWeak";
    PasswordStrength[PasswordStrength["Weak"] = 2] = "Weak";
    PasswordStrength[PasswordStrength["Good"] = 3] = "Good";
    PasswordStrength[PasswordStrength["Strong"] = 4] = "Strong";
    PasswordStrength[PasswordStrength["VeryStrong"] = 5] = "VeryStrong";
})(PasswordStrength || (PasswordStrength = {}));
const defaultPasswordStrengthConfig = {
    enableLengthRule: true,
    enableLowerCaseLetterRule: true,
    enableUpperCaseLetterRule: true,
    enableDigitRule: true,
    enableSpecialCharRule: true,
    min: 8,
};
// eslint-disable-next-line sonarjs/cognitive-complexity
function checkPassword(pwd, { enableLengthRule, enableLowerCaseLetterRule, enableUpperCaseLetterRule, enableSpecialCharRule, enableDigitRule, min, } = defaultPasswordStrengthConfig) {
    // Simultaneous variable declaration and value assignment aren't supported in IE apparently
    // so I'm forced to assign the same value individually per var to support a crappy browser *sigh*
    let nScore = 0;
    let nLength = 0;
    let nAlphaUC = 0;
    let nAlphaLC = 0;
    let nNumber = 0;
    let nSymbol = 0;
    let nMidChar = 0;
    let nRequirements = 0;
    let nUnqChar = 0;
    let nRepChar = 0;
    let nRepInc = 0;
    let nConsecAlphaUC = 0;
    let nConsecAlphaLC = 0;
    let nConsecNumber = 0;
    let nSeqAlpha = 0;
    let nSeqNumber = 0;
    let nSeqSymbol = 0;
    let nReqChar = 0;
    const nMultMidChar = 2;
    const nMultConsecAlphaUC = 2;
    const nMultConsecAlphaLC = 2;
    const nMultConsecNumber = 2;
    const nMultSeqAlpha = 3;
    const nMultSeqNumber = 3;
    const nMultSeqSymbol = 3;
    const nMultLength = 4;
    const nMultNumber = 4;
    const nMultSymbol = 6;
    let nTmpAlphaUC = -1;
    let nTmpAlphaLC = -1;
    let nTmpNumber = -1;
    let nTmpSymbol = -1;
    const sAlphas = 'abcdefghijklmnopqrstuvwxyz';
    const sNumerics = '01234567890';
    const sSymbols = ')!@#$%^&*()';
    const nMinPwdLen = min;
    let sComplexity = PasswordStrength.TooShort;
    if (pwd) {
        nScore = pwd.length * nMultLength;
        nLength = pwd.length;
        const arrPwd = pwd.replace(/\s+/g, '').split(/\s*/);
        const arrPwdLen = arrPwd.length;
        // Loop through password to check for Symbol, Numeric, Lowercase and Uppercase pattern matches
        for (let a = 0; a < arrPwdLen; a++) {
            if (arrPwd[a].match(/[A-Z]/g)) {
                if (nTmpAlphaUC !== -1 && nTmpAlphaUC + 1 === a) {
                    nConsecAlphaUC++;
                }
                nTmpAlphaUC = a;
                nAlphaUC++;
            }
            else if (arrPwd[a].match(/[a-z]/g)) {
                if (nTmpAlphaLC !== -1 && nTmpAlphaLC + 1 === a) {
                    nConsecAlphaLC++;
                }
                nTmpAlphaLC = a;
                nAlphaLC++;
            }
            else if (arrPwd[a].match(/[0-9]/g)) {
                if (a > 0 && a < arrPwdLen - 1) {
                    nMidChar++;
                }
                if (nTmpNumber !== -1 && nTmpNumber + 1 === a) {
                    nConsecNumber++;
                }
                nTmpNumber = a;
                nNumber++;
            }
            else if (arrPwd[a].match(/[^a-zA-Z0-9_]/g)) {
                if (a > 0 && a < arrPwdLen - 1) {
                    nMidChar++;
                }
                if (nTmpSymbol !== -1 && nTmpSymbol + 1 === a) {
                }
                nTmpSymbol = a;
                nSymbol++;
            }
            // Internal loop through password to check for repeat characters
            let bCharExists = false;
            for (let b = 0; b < arrPwdLen; b++) {
                if (arrPwd[a] === arrPwd[b] && a !== b) {
                    // repeat character exists
                    bCharExists = true;
                    /*
                              Calculate icrement deduction based on proximity to identical characters
                              Deduction is incremented each time a new match is discovered
                              Deduction amount is based on total password length divided by the
                              difference of distance between currently selected match
                              */
                    nRepInc += Math.abs(arrPwdLen / (b - a));
                }
            }
            if (bCharExists) {
                nRepChar++;
                nUnqChar = arrPwdLen - nRepChar;
                nRepInc = nUnqChar ? Math.ceil(nRepInc / nUnqChar) : Math.ceil(nRepInc);
            }
        }
        // Check for sequential alpha string patterns (forward and reverse)
        for (let s = 0; s < 23; s++) {
            const sFwd = sAlphas.substring(s, s + 3);
            const sRev = reverseString(sFwd);
            if (pwd.toLowerCase().indexOf(sFwd) !== -1 || pwd.toLowerCase().indexOf(sRev) !== -1) {
                nSeqAlpha++;
            }
        }
        // Check for sequential numeric string patterns (forward and reverse)
        for (let s = 0; s < 8; s++) {
            const sFwd = sNumerics.substring(s, s + 3);
            const sRev = reverseString(sFwd);
            if (pwd.toLowerCase().indexOf(sFwd) !== -1 || pwd.toLowerCase().indexOf(sRev) !== -1) {
                nSeqNumber++;
            }
        }
        // Check for sequential symbol string patterns (forward and reverse)
        for (let s = 0; s < 8; s++) {
            const sFwd = sSymbols.substring(s, s + 3);
            const sRev = reverseString(sFwd);
            if (pwd.toLowerCase().indexOf(sFwd) !== -1 || pwd.toLowerCase().indexOf(sRev) !== -1) {
                nSeqSymbol++;
            }
        }
        // Modify overall score value based on usage vs requirements
        // General point assignment
        if (nAlphaUC > 0 && nAlphaUC < nLength) {
            nScore = nScore + (nLength - nAlphaUC) * 2;
        }
        if (nAlphaLC > 0 && nAlphaLC < nLength) {
            nScore = nScore + (nLength - nAlphaLC) * 2;
        }
        if (nNumber > 0 && nNumber < nLength) {
            nScore = nScore + nNumber * nMultNumber;
        }
        if (nSymbol > 0) {
            nScore = nScore + nSymbol * nMultSymbol;
        }
        if (nMidChar > 0) {
            nScore = nScore + nMidChar * nMultMidChar;
        }
        // Point deductions for poor practices
        if ((nAlphaLC > 0 || nAlphaUC > 0) && nSymbol === 0 && nNumber === 0) {
            // Only Letters
            nScore = nScore - nLength;
        }
        if (nAlphaLC === 0 && nAlphaUC === 0 && nSymbol === 0 && nNumber > 0) {
            // Only Numbers
            nScore = nScore - nLength;
        }
        if (nRepChar > 0) {
            // Same character exists more than once
            nScore = nScore - nRepInc;
        }
        if (nConsecAlphaUC > 0) {
            // Consecutive Uppercase Letters exist
            nScore = nScore - nConsecAlphaUC * nMultConsecAlphaUC;
        }
        if (nConsecAlphaLC > 0) {
            // Consecutive Lowercase Letters exist
            nScore = nScore - nConsecAlphaLC * nMultConsecAlphaLC;
        }
        if (nConsecNumber > 0) {
            // Consecutive Numbers exist
            nScore = nScore - nConsecNumber * nMultConsecNumber;
        }
        if (nSeqAlpha > 0) {
            // Sequential alpha strings exist (3 characters or more)
            nScore = nScore - nSeqAlpha * nMultSeqAlpha;
        }
        if (nSeqNumber > 0) {
            // Sequential numeric strings exist (3 characters or more)
            nScore = nScore - nSeqNumber * nMultSeqNumber;
        }
        if (nSeqSymbol > 0) {
            // Sequential symbol strings exist (3 characters or more)
            nScore = nScore - nSeqSymbol * nMultSeqSymbol;
        }
        // Determine if mandatory requirements have been met and set image indicators accordingly
        const arrChars = [nLength, nAlphaUC, nAlphaLC, nNumber, nSymbol];
        const arrCharsIds = [
            enableLengthRule ? 'nLength' : null,
            enableUpperCaseLetterRule ? 'nAlphaUC' : null,
            enableLowerCaseLetterRule ? 'nAlphaLC' : null,
            enableDigitRule ? 'nNumber' : null,
            enableSpecialCharRule ? 'nSymbol' : null,
        ];
        const arrCharsLen = arrChars.length;
        for (let c = 0; c < arrCharsLen; c++) {
            const minVal = arrCharsIds[c] === 'nLength' ? nMinPwdLen - 1 : 0;
            if (arrChars[c] === minVal + 1) {
                nReqChar++;
            }
            else if (arrChars[c] > minVal + 1) {
                nReqChar++;
            }
        }
        nRequirements = nReqChar;
        const nMinReqChars = pwd.length >= nMinPwdLen ? 3 : 4;
        if (nRequirements > nMinReqChars) {
            // One or more required characters exist
            nScore = nScore + nRequirements * 2;
        }
        // Determine complexity based on overall score
        if (nScore > 100) {
            nScore = 100;
        }
        else if (nScore < 0) {
            nScore = 0;
        }
        if (nScore >= 0 && nScore < 20) {
            sComplexity = PasswordStrength.VeryWeak;
        }
        else if (nScore >= 20 && nScore < 40) {
            sComplexity = PasswordStrength.Weak;
        }
        else if (nScore >= 40 && nScore < 60) {
            sComplexity = PasswordStrength.Good;
        }
        else if (nScore >= 60 && nScore < 80) {
            sComplexity = PasswordStrength.Strong;
        }
        else if (nScore >= 80 && nScore <= 100) {
            sComplexity = PasswordStrength.VeryStrong;
        }
        // Display updated score criteria to client
        return { score: nScore, complexity: sComplexity };
    }
    return { score: nScore, complexity: sComplexity };
}

class MonitorService {
    getCurrentRouteName(childrenOrigin) {
        let children = childrenOrigin;
        let tmpUrl = '';
        while (children.length > 0) {
            if (children[0].routeConfig.path !== '') {
                tmpUrl += `/${children[0].routeConfig.path}`;
            }
            children = children[0].children;
        }
        return tmpUrl;
    }
}

class MonitorWebservice {
    constructor(url) {
        this.monitorURL = url.replace(/\/$/, '');
    }
    post(metric) {
        const url = buildUrl(`${this.monitorURL}/metrics`);
        return from(axios.post(url, metric));
    }
}

function getMessagesStream(eventSource) {
    return new Observable((observer) => {
        eventSource.onmessage = (event) => observer.next(event.data);
        eventSource.onerror = (event) => {
            // EventSource.OPEN         no error, connection opened
            // EventSource.CONNECTING   connecting or error occured but browser is trying to reconnect
            // EventSource.CLOSED       error was fatal
            if (event.target.readyState === EventSource.CLOSED) {
                observer.error(event);
            }
        };
        const close = eventSource.close;
        eventSource.close = () => {
            close.call(eventSource);
            observer.complete();
        };
    });
}

class OembedProviderInterface {
    getId(url) {
        const matches = this.urls
            .map((pattern) => {
            // 1. Try to match without stripping the protocol and "www" subdomain.
            let match = url.match(pattern);
            if (match) {
                return match[1];
            }
            // 2. Try to match after stripping the protocol.
            let rawUrl = url.replace(/^https?:\/\//, '');
            match = rawUrl.match(pattern);
            if (match) {
                return match[1];
            }
            // 3. Try to match after stripping the "www" subdomain.
            rawUrl = rawUrl.replace(/^www\./, '');
            match = rawUrl.match(pattern);
            if (match) {
                return match[1];
            }
            return null;
        })
            .filter((id) => id !== null);
        return matches && matches.length > 0 ? matches[0] : null;
    }
}

class DailymotionProvider extends OembedProviderInterface {
    constructor() {
        super(...arguments);
        this.name = 'dailymotion';
        this.urls = [/^dailymotion\.com\/video\/(\w+)/];
    }
    getVideo(id) {
        return ('<div style="position: relative; padding-bottom: 100%; height: 0; ">' +
            `<iframe src="https://www.dailymotion.com/embed/video/${id}" ` +
            'style="position: absolute; width: 100%; height: 100%; top: 0; left: 0;" ' +
            'frameborder="0" width="480" height="270" allowfullscreen allow="autoplay">' +
            '</iframe>' +
            '</div>');
    }
}

class YoutubeProvider extends OembedProviderInterface {
    constructor() {
        super(...arguments);
        this.name = 'youtube';
        this.urls = [
            /^(?:m\.)?youtube\.com\/watch\?v=([\w-]+)/,
            /^(?:m\.)?youtube\.com\/v\/([\w-]+)/,
            /^youtube\.com\/embed\/([\w-]+)/,
            /^youtu\.be\/([\w-]+)/,
        ];
    }
    getVideo(id) {
        return ('<div style="position: relative; padding-bottom: 100%; height: 0; padding-bottom: 56.2493%;">' +
            `<iframe src="https://www.youtube.com/embed/${id}" ` +
            'style="position: absolute; width: 100%; height: 100%; top: 0; left: 0;" ' +
            'frameborder="0" allow="autoplay; encrypted-media" allowfullscreen>' +
            '</iframe>' +
            '</div>');
    }
}

class OembedPipe {
    constructor() {
        this.providers = [new YoutubeProvider(), new DailymotionProvider()];
    }
    transform(htmlContent) {
        let content = htmlContent;
        const oembedUrls = htmlContent.match(/<\s*oembed[^>]*>(.*?)<\s*\/\s*oembed>/g);
        if (!oembedUrls) {
            return content;
        }
        const iframes = oembedUrls
            .map((oembedUrl) => oembedUrl.split('url="')[1].split('"')[0])
            .map((url) => {
            let videoIframe = null;
            this.providers.every((provider) => {
                const id = provider.getId(url);
                if (!id) {
                    return true;
                }
                videoIframe = provider.getVideo(id);
                return false;
            });
            return videoIframe;
        });
        oembedUrls.forEach((oembedUrl, index) => {
            if (iframes[index]) {
                content = content.replace(oembedUrl, iframes[index]);
            }
        });
        return content;
    }
}
OembedPipe.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.0.1", ngImport: i0, type: OembedPipe, deps: [], target: i0.ɵɵFactoryTarget.Pipe });
OembedPipe.ɵpipe = i0.ɵɵngDeclarePipe({ minVersion: "14.0.0", version: "14.0.1", ngImport: i0, type: OembedPipe, name: "oembed" });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.0.1", ngImport: i0, type: OembedPipe, decorators: [{
            type: Pipe,
            args: [{
                    name: 'oembed',
                }]
        }] });
class OembedModule {
}
OembedModule.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.0.1", ngImport: i0, type: OembedModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
OembedModule.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "14.0.1", ngImport: i0, type: OembedModule, declarations: [OembedPipe], imports: [CommonModule], exports: [OembedPipe] });
OembedModule.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "14.0.1", ngImport: i0, type: OembedModule, providers: [OembedPipe], imports: [CommonModule] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.0.1", ngImport: i0, type: OembedModule, decorators: [{
            type: NgModule,
            args: [{
                    declarations: [OembedPipe],
                    exports: [OembedPipe],
                    providers: [OembedPipe],
                    imports: [CommonModule],
                }]
        }] });

const catchNotFound = (router, notFoundPage) => (source) => source.pipe(catchError((error) => {
    if (error.error.status === 404 || error.status === 404) {
        router.navigate(notFoundPage, { skipLocationChange: true });
    }
    return throwError(error);
}));

/* eslint no-console: ["error", { allow: ["warn", "error"] }] */
function getRuntimeEnvVar(key, defaultValue) {
    if (!process.env.USE_SERVER_VARS) {
        return defaultValue;
    }
    if (typeof window === 'object') {
        if (!window['env']) {
            throw new Error('window.env is not defined. Have you run inject-env script ?');
        }
        if (!window['env'][key]) {
            console.warn(`window['env']['${key}'] is undefined. Provide it in your helm chart & add the key to the variable injection whitelist.`);
        }
        return window['env'][key];
    }
    if (typeof global === 'object') {
        if (!global['ANGULAR_ENV']) {
            console.warn('global.ANGULAR_ENV has not been set in SSR environment. Define it in your entrypoint if you need it.');
        }
        return (global['ANGULAR_ENV'] || {})[key];
    }
}
function getBuildEnvVar(key, defaultValue) {
    if (!process.env.USE_SERVER_VARS) {
        return defaultValue;
    }
    if (!process.env[key]) {
        throw new Error(`${key} variable has not been defined in your webpack build configuration.`);
    }
    return process.env[key];
}

/**
 * Generated bundle index. Do not edit.
 */

export { AbstractToFormControlPipe, AbstractToFormControlPipeModule, AosDirective, AosModule, AppBrowserViewportScroller, AppNullViewportScroller, AutofocusDirective, BrowserWindowRef, DataStorage, DataStorageService, GravityEnum, IMAGINARY_PIPE_CONFIG, ImageService, ImaginarySrcModule, ImaginarySrcPipe, KeysPipe, KeysPipeModule, LazyImgDirective, LazyImgModule, LazyVideoDirective, LazyVideoModule, LinkyPipe, LinkyPipeModule, MonitorService, MonitorWebservice, OembedModule, OembedPipe, OperationTypes, PasswordStrength, SlugifyPipe, SlugifyPipeModule, WINDOW, WINDOW_PROVIDERS, WindowRef, WizAutofocusModule, WizPipesModule, addService, buildPath, buildUrl, catchNotFound, checkPassword, collect, downloadFile, getAge, getBuildEnvVar, getMessagesStream, getRuntimeEnvVar, isParamInSpecificPlaceInPath, readBlob, windowFactory };
